import React, { useState, useEffect } from 'react';
import { OPERATION_TYPES_LABELS } from '../../../constants/operationTypes';
import api from '../../../api';
import { motion, AnimatePresence } from 'framer-motion';
import CustomSelect from '../../../components/ui/CustomSelect';

export default function DossierForm({ isOpen, onClose, onSubmit }) {
 const [formData, setFormData] = useState({
 title: '',
 operationType: 'REAL_ESTATE_CLUB_DEAL',
 requestedAmount: '',
 estimatedAssetValue: '',
 familleId: '',
 societeId: '',
 prospectName: '',
 location: '',
 currency: 'EUR',
 priority: 'MEDIUM',
 riskLevel: 'MEDIUM'
 });
 
 const [familles, setFamilles] = useState([]);
 const [societes, setSocietes] = useState([]);

 useEffect(() => {
 if (isOpen) {
 const fetchRelations = async () => {
 try {
 const resFam = await api.get('/familles');
 setFamilles(resFam.data);
 const resSoc = await api.get('/societes');
 setSocietes(resSoc.data);
 } catch (error) {
 console.error("Erreur lors du chargement des relations", error);
 }
 };
 fetchRelations();
 }
 }, [isOpen]);

 // Note: on gère maintenant l'affichage avec AnimatePresence dans le JSX
 // if (!isOpen) return null;

 const handleChange = (e) => {
 const { name, value } = e.target;
 setFormData(prev => ({ ...prev, [name]: value }));
 };

 const handleClientSelect = (name, value) => {
 setFormData(prev => {
 const next = { ...prev, [name]: value };
 // Reset des autres si on sélectionne Famille/Société ou Prospect
 if (name === 'familleId' && value) {
 next.societeId = '';
 next.prospectName = '';
 } else if (name === 'societeId' && value) {
 next.familleId = '';
 next.prospectName = '';
 }
 return next;
 });
 };

 const handleSubmit = (e) => {
 e.preventDefault();
 onSubmit(formData);
 };

 const typeOptions = Object.entries(OPERATION_TYPES_LABELS).map(([key, label]) => ({
 value: key,
 label: label
 }));

 const priorityOptions = [
 { value: 'LOW', label: 'Basse' },
 { value: 'MEDIUM', label: 'Moyenne' },
 { value: 'HIGH', label: 'Haute' },
 { value: 'CRITICAL', label: 'Critique' }
 ];

 const riskOptions = [
 { value: 'LOW', label: 'Faible' },
 { value: 'MEDIUM', label: 'Moyen' },
 { value: 'HIGH', label: 'Élevé' },
 { value: 'CRITICAL', label: 'Critique' }
 ];

 const familleOptions = familles.map(f => ({
 value: f.id,
 label: `${f.nom} ${f.prenom || ''}`.trim()
 }));

 const societeOptions = societes.map(s => ({
 value: s.id,
 label: s.denomination
 }));

 return (
 <AnimatePresence>
 {isOpen && (
 <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
 <motion.div 
 initial={{ opacity: 0, scale: 0.95 }}
 animate={{ opacity: 1, scale: 1 }}
 exit={{ opacity: 0, scale: 0.95 }}
 transition={{ duration: 0.2, ease: 'easeOut' }}
 className="workspace-card p-6 w-full max-w-lg shadow-hp-lg"
 >
 <h2 className="text-xl font-bold text-primary mb-6">Nouveau Dossier</h2>
 
 <form onSubmit={handleSubmit} className="space-y-5">
 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Titre de l'opération *</label>
 <input type="text" name="title" required value={formData.title} onChange={handleChange} className="workspace-input" placeholder="Ex: Rénovation 45 Rue de la Paix" />
 </div>
 
 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Type d'opération *</label>
 <CustomSelect 
 value={formData.operationType} 
 onChange={(val) => setFormData(prev => ({ ...prev, operationType: val }))} 
 options={typeOptions}
 placeholder="Sélectionner le type"
 />
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Montant recherché</label>
 <input type="number" name="requestedAmount" value={formData.requestedAmount} onChange={handleChange} className="workspace-input" placeholder="€" />
 </div>
 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Valeur estimée</label>
 <input type="number" name="estimatedAssetValue" value={formData.estimatedAssetValue} onChange={handleChange} className="workspace-input" placeholder="€" />
 </div>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Localisation</label>
 <input type="text" name="location" value={formData.location} onChange={handleChange} className="workspace-input" placeholder="Ex: Paris, France" />
 </div>
 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Devise</label>
 <input type="text" name="currency" value={formData.currency} onChange={handleChange} className="workspace-input" placeholder="EUR" />
 </div>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Priorité</label>
 <CustomSelect 
 value={formData.priority} 
 onChange={(val) => setFormData(prev => ({ ...prev, priority: val }))} 
 options={priorityOptions}
 />
 </div>
 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Niveau de Risque</label>
 <CustomSelect 
 value={formData.riskLevel} 
 onChange={(val) => setFormData(prev => ({ ...prev, riskLevel: val }))} 
 options={riskOptions}
 />
 </div>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Client (Famille)</label>
 <CustomSelect 
 value={formData.familleId} 
 onChange={(val) => handleClientSelect('familleId', val)} 
 options={familleOptions}
 placeholder="-- Aucune --"
 />
 </div>
 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Client (Société)</label>
 <CustomSelect 
 value={formData.societeId} 
 onChange={(val) => handleClientSelect('societeId', val)} 
 options={societeOptions}
 placeholder="-- Aucune --"
 />
 </div>
 </div>

 <div>
 <label className="block text-sm font-semibold text-primary mb-1">
 Prospect / Initiateur externe
 <span className="text-xs text-tertiary ml-2 font-normal">(Si non rattaché à un client)</span>
 </label>
 <input 
 type="text" 
 name="prospectName" 
 value={formData.prospectName} 
 onChange={(e) => {
 setFormData(prev => ({ ...prev, prospectName: e.target.value, familleId: '', societeId: '' }));
 }} 
 className="workspace-input" 
 placeholder="Ex: Jean Dupont" 
 />
 </div>

 <div className="flex justify-end space-x-3 pt-6 border-t border-workspace mt-6">
 <button type="button" onClick={onClose} className="btn btn-secondary">Annuler</button>
 <button type="submit" className="btn btn-primary">Créer le dossier</button>
 </div>
 </form>
 </motion.div>
 </div>
 )}
 </AnimatePresence>
 );
}
